import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { StoreProvider } from "./store";
import { Navbar, Footer, WhatsAppFloating } from "./Layout";
import ScrollToTop from "./ScrollToTop";
import DeferredConcierge from "./DeferredConcierge";
import HomePage from "./pages/HomePage";        // Home eagerly loaded for fast LCP
import {
  PrivacyPage, ShippingPage, ReturnsPolicyPage, TermsPage,
  AboutPage, ContactPage, FaqPage, TrackOrderPage,
  ForgotPasswordPage, ResetPasswordPage,
} from "./pages/StaticPages";

// Heavier / interactive pages code-split
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));

function PageFallback() {
  return <div className="p-24 text-center opacity-50 text-sm" style={{color:"var(--primary)"}}>Loading…</div>;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) return (
    <Suspense fallback={<PageFallback/>}><AuthCallback /></Suspense>
  );
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Suspense fallback={<PageFallback/>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/shop/:gender" element={<ShopPage />} />
            <Route path="/shop/:gender/:category" element={<ShopPage />} />
            <Route path="/collection/:collection" element={<CollectionPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-success/:orderId" element={<OrderSuccessPage />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/track" element={<TrackOrderPage />} />
            <Route path="/policy/privacy" element={<PrivacyPage />} />
            <Route path="/policy/shipping" element={<ShippingPage />} />
            <Route path="/policy/returns" element={<ReturnsPolicyPage />} />
            <Route path="/policy/terms" element={<TermsPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <WhatsAppFloating />
      <DeferredConcierge />
    </>
  );
}

function App() {
  useEffect(() => {
    // Only load Razorpay checkout script on checkout page to save initial payload
    const load = () => {
      if (!document.getElementById("razorpay-script") && window.location.pathname.startsWith("/checkout")) {
        const s = document.createElement("script");
        s.id = "razorpay-script"; s.src = "https://checkout.razorpay.com/v1/checkout.js"; s.async = true;
        document.body.appendChild(s);
      }
    };
    load();
    window.addEventListener("popstate", load);
    return () => window.removeEventListener("popstate", load);
  }, []);
  return (
    <div className="App">
      <StoreProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <AppRouter />
        </BrowserRouter>
      </StoreProvider>
    </div>
  );
}

export default App;
